import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import { getCategories } from "@insite/client-framework/Services/CategoryService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showImages = "showImages",
}

interface Props extends WidgetProps, HasCategoryContext {}

export interface CategoryDetailSubCategoriesStyles {
    container?: GridContainerProps;
    subCategoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    subCategoryImageItem?: GridItemProps;
    subCategoryImageClickable?: ClickablePresentationProps;
    subCategoryImage?: LazyImageProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    wrapperStyles?: InjectableCss;
    lineDividerStyles?: InjectableCss;
    overlay?: InjectableCss;
    cardContainer?: InjectableCss;
}

export const categoryDetailSubCategoriesStyles: CategoryDetailSubCategoriesStyles = {
    container: {
        gap: 20,
    },
    subCategoryItem: {
        width: [12, 12, 4, 3, 3],
        css: css`
            padding-bottom: 30px;
        `,
    },
    innerContainer: {
        gap: 0,
    },
    subCategoryImageItem: {
        css: css`
            width: 100%;
            padding: 0 8px 8px 8px;
            flex: 1 0 183px;
        `,
    },
    subCategoryImageClickable: {
        css: css`
            width: 100%;
        `,
    },
    subCategoryImage: {
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    subCategoryNameLinkItem: {
        css: css`
            display: flex;
            flex: 0 1 63px;
            padding-bottom: 8px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        justify-content: center;
                    `,
                    css`
                        justify-content: center;
                    `,
                    css`
                        justify-content: center;
                    `,
                ])}
        `,
    },
    subCategoryNameLink: {
        css: css`
            width: 100%;
        `,
        color: "text.main",
        typographyProps: {
            weight: "bold",
            css: css`
                width: 100%;
                text-align: center;
                word-wrap: break-word;
            `,
        },
    },
    lineDividerStyles: {
        css: css`
            margin-top: 1rem;
            margin-bottom: 1rem;
            margin-left: 10%;
            margin-right: 10%;
            border-top: 2px #016eaf solid;
            flex; 1 0 2px;
        `,
    },
    wrapperStyles: {
        css: css`
            width: 100%;
            text-align: center;
            justify-content: center;
            min-height: 100%;
            display: flex;
            flex-direction: column;
        `,
    },
    overlay: {
        css: css`
            position: absolute;
            color: rgba(255, 255, 255, 0);
            font-weight: bold;
            display: flex;
            flex-direction: column;
            justify-content: center;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            height: 100%;
            width: 100%;
            transition: 0.5s ease;
            background-color: rgba(0, 0, 0, 0);
            &:hover {
                color: rgba(255, 255, 255, 1);
                background-color: rgba(0, 0, 0, 0.75);
            }
        `,
    },

    cardContainer: {
        css: css`
            position: relative;
            width: 100%;
            text-align: center;
            justify-content: center;
            min-height: 100%;
            padding-top: 2rem;
            border: 1px black solid;
        `,
    },
};

const styles = categoryDetailSubCategoriesStyles;

const GerrieCategoryDetailSubCategories: React.FC<Props> = props => {
    const [categories, setCategories] = React.useState<any>();
    const [isLoading, setIsLoading] = React.useState(true);

    React.useEffect(() => {
        getCategories({ maxDepth: 5 }).then(res => {
            setCategories(res);
            setIsLoading(false);
        });
    }, []);

    if (!props.category || !props.category.subCategoryIds || props.category.subCategoryIds.length === 0) {
        return null;
    }

    const {
        fields: { showImages },
    } = props;

    return !isLoading ? (
        <GridContainer {...styles.container}>
            {props.category.subCategoryIds.map(subCategoryId => (
                <GridItem key={subCategoryId} {...styles.subCategoryItem}>
                    <SubCategoryLink categoryId={subCategoryId} showImages={showImages} categories={categories} />
                </GridItem>
            ))}
        </GridContainer>
    ) : (
        <></>
    );
};

const SubCategoryLinkView = ({
    category,
    showImages,
    categories,
}: ReturnType<typeof mapStateToProps> & { showImages: boolean; categories: any }) => {
    if (!category) {
        return null;
    }

    const subcategories: string[] =
        categories.categoriesById[category.id] &&
        categories.categoriesById[category.id].subCategoryIds &&
        categories.categoriesById[category.id].subCategoryIds
            .map((item: string | number) => categories.categoriesById[item].name)
            .sort();

    const renderSubcategoriesList = (subcategoriesToRender: string[]) => {
        if (subcategoriesToRender.length > 4) {
            return [...subcategoriesToRender.slice(0, 4).map(item => <StyledWrapper key={`${category.id}${item}`}>{item}</StyledWrapper>), <StyledWrapper key={`${category.id}more`}>More...</StyledWrapper>];
        }
        return subcategoriesToRender.map(item => <StyledWrapper key={`${category.id}${item}`}>{item}</StyledWrapper>);
    };

    return (
        <Clickable href={category.path} {...styles.cardContainer}>
            <StyledWrapper {...styles.wrapperStyles}>
                {showImages && (
                    <StyledWrapper {...styles.subCategoryImageItem}>
                        {category.smallImagePath && (
                            <Clickable href={category.path} {...styles.subCategoryImageClickable}>
                                <LazyImage
                                    src={category.smallImagePath}
                                    altText={category.imageAltText}
                                    {...styles.subCategoryImage}
                                />
                            </Clickable>
                        )}
                    </StyledWrapper>
                )}

                <StyledWrapper {...styles.lineDividerStyles}> </StyledWrapper>

                <StyledWrapper {...styles.subCategoryNameLinkItem}>
                    <Link
                        href={category.path}
                        {...styles.subCategoryNameLink}
                        data-test-selector={`categoryDetailsSubCategoriesLink_${category.id}`}
                    >
                        {category.shortDescription}
                    </Link>
                </StyledWrapper>
            </StyledWrapper>
            {subcategories && subcategories.length > 0 && (
                <StyledWrapper {...styles.overlay}>
                    <StyledWrapper>{renderSubcategoriesList(subcategories)}</StyledWrapper>
                </StyledWrapper>
            )}
        </Clickable>
    );
};

const mapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
});

const SubCategoryLink = connect(mapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: withCategory(GerrieCategoryDetailSubCategories),
    definition: {
        displayName: "Gerrie Category Detail Sub Categories",
        group: "Categories",
        icon: "LinkList",
        fieldDefinitions: [
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
